import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StoreResponse, StoreResponseError, StoreResponseSuccess } from '@p2p/shared/domain';
import { filter, map, Observable, switchMap, take } from 'rxjs';

import {
    PaymentProfile,
    PaymentProfileCreateRequest,
    PaymentProfileEditRequest,
    PaymentProfileStatus
} from '../../entities';

import * as ProfilesActions from './profiles.actions';
import { ProfilesPageParams } from './profiles.model';
import * as ProfilesSelectors from './profiles.selectors';

@Injectable()
export class ProfilesFacade {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    list$ = this.store.select(ProfilesSelectors.selectPaymentProfilesList);
    loading$ = this.store.select(ProfilesSelectors.selectLoading);
    loaded$ = this.store.select(ProfilesSelectors.selectLoaded);
    loadError$ = this.store.select(ProfilesSelectors.selectLoadError);
    pageParams$ = this.store.select(ProfilesSelectors.selectPageParams);
    totalElements$ = this.store.select(ProfilesSelectors.selectTotalElements);
    error$ = this.store.select(ProfilesSelectors.selectError);
    idsInProgress$ = this.store.select(ProfilesSelectors.selectIdsInProgress);

    loadProfiles(pageParams: ProfilesPageParams = {}): void {
        this.store.dispatch(ProfilesActions.loadPaymentProfiles({ pageParams }));
    }

    setPageParams(pageParams: ProfilesPageParams): void {
        this.store.dispatch(ProfilesActions.setPageParams({ pageParams }));
    }

    deleteProfile(id: number): void {
        this.store.dispatch(ProfilesActions.deleteProfile({ id }));
    }

    changeProfileStatus(id: number, status: PaymentProfileStatus): void {
        this.store.dispatch(ProfilesActions.changeProfileStatus({ id, status }));
    }

    createProfile(profile: PaymentProfileCreateRequest): void {
        this.store.dispatch(ProfilesActions.createProfile({ profile }));
    }

    editProfile(profile: PaymentProfileEditRequest): void {
        this.store.dispatch(ProfilesActions.editProfile({ profile }));
    }

    getErrorAfterProgress(id: number): Observable<HttpErrorResponse | null> {
        return this.idsInProgress$.pipe(
            filter((idsInProgress) => !idsInProgress.includes(id)),
            switchMap(() => this.error$),
            take(1)
        );
    }

    getDeleteProfileResponse(): Observable<StoreResponse<unknown>> {
        return this.actions$.pipe(
            ofType(ProfilesActions.deleteProfileSuccess, ProfilesActions.deleteProfileFailure),
            map((action) => {
                if (action.type === ProfilesActions.deleteProfileSuccess.type) {
                    return new StoreResponseSuccess<unknown>();
                }

                return new StoreResponseError(action.error);
            }),
            take(1)
        );
    }

    getCreateProfileResponse(): Observable<StoreResponse<PaymentProfile>> {
        return this.actions$.pipe(
            ofType(ProfilesActions.createProfileSuccess, ProfilesActions.createProfileFailure),
            map((action) => {
                if (action.type === ProfilesActions.createProfileSuccess.type) {
                    return new StoreResponseSuccess<PaymentProfile>(action.profile);
                }

                return new StoreResponseError(action.error);
            }),
            take(1)
        );
    }

    getEditProfileResponse(): Observable<StoreResponse<PaymentProfile>> {
        return this.actions$.pipe(
            ofType(ProfilesActions.editProfileSuccess, ProfilesActions.editProfileFailure),
            map((action) => {
                if (action.type === ProfilesActions.editProfileSuccess.type) {
                    return new StoreResponseSuccess<PaymentProfile>(action.profile);
                }

                return new StoreResponseError(action.error);
            }),
            take(1)
        );
    }
}
